/**
 * Проверяет, входит ли сезон (год) в диапазон [from, to].
 * null означает бесконечность: [null, 2024] = "до 2024 включительно",
 * [2026, null] = "с 2026 и далее".
 */
const isSeasonInRange = (season, [from, to]) => {
  if (from !== null && season < from) return false;
  if (to !== null && season > to) return false;
  return true;
};

/**
 * Возвращает контур (geometry) из field.geometries,
 * актуальный для указанного сезона.
 * Если geometries нет или контур не найден — возвращает null.
 */
const getGeometryBySeason = (field, season) => {
  if (!field?.geometries?.length || season == null) return null;

  return (
    field.geometries.find((geom) =>
      (geom.geometry_seasons || []).some((range) =>
        isSeasonInRange(Number(season), range)
      )
    ) || null
  );
};

/**
 * Возвращает area для поля с учётом сезона.
 * Сначала ищет в geometries, fallback на field.area.
 */
export const getFieldAreaBySeason = (field, season) => {
  const geometry = getGeometryBySeason(field, season);
  return geometry?.area ?? field?.area ?? null;
};

/**
 * Возвращает svg_icon_path для поля с учётом сезона.
 * Сначала ищет в geometries, fallback на field.svg_icon_path.
 */
export const getFieldSvgIconBySeason = (field, season) => {
  const geometry = getGeometryBySeason(field, season);
  return geometry?.svg_icon_path ?? field?.svg_icon_path ?? null;
};

export const FIELD_GEOMETRIES_CADASTER_EMBED =
  'geometries,geometries.cadaster_intersections';

// The field page reads the field with its season contours and cadaster
// intersections; reloads after changes must keep them (without the embed the
// field has no geometries).
export const FIELD_WITH_GEOMETRIES_PARAMS = {
  embed: FIELD_GEOMETRIES_CADASTER_EMBED,
};

const CADASTER_LINK_TYPE_INTERSECTION = 1;

/**
 * Пересечения контура поля с кадастровыми участками для выбранного сезона.
 */
export const getCadasterIntersectionsBySeason = (field, season) => {
  const geometry = getGeometryBySeason(field, season);
  return (geometry?.cadaster_intersections || []).filter(
    (intersection) => intersection.link_type === CADASTER_LINK_TYPE_INTERSECTION
  );
};

/**
 * id контуров полей, актуальных для выбранного сезона.
 */
export const getSeasonGeometryIds = (fields, season) => {
  const ids = new Set();
  (fields || []).forEach((field) => {
    const geometry = getGeometryBySeason(field, season);
    if (geometry?.id != null) {
      ids.add(Number(geometry.id));
    }
  });
  return ids;
};

/**
 * Пересечения КУ с полями, относящиеся к контурам выбранного сезона.
 */
export const filterFieldsIntersectionsBySeason = (intersections, geometryIds) =>
  (intersections || []).filter(
    (intersection) =>
      intersection.link_type === CADASTER_LINK_TYPE_INTERSECTION &&
      intersection.field_geometry_id != null &&
      geometryIds.has(Number(intersection.field_geometry_id))
  );

export default getGeometryBySeason;
